import { createWipeOrigin } from "@scripts/wipeOrigin";

import { createDisclosure } from "./SiteHeader.disclosure";
import { focusFirstStop, tabStops, trackFocusWithin } from "./SiteHeader.focus";
import { createPanel } from "./SiteHeader.panel";
import { EBar, EPanel, getHeaderStore } from "./SiteHeader.store";

const MEGA = "[data-fa-mega-menu]";
const UTILITY_ROW = "[data-fa-header-utility]";
const BAR = "[data-fa-header-bar]";
const ORIGIN = "data-fa-mega-menu-origin";
const SHEET = "[data-fa-mega-menu-sheet]";
const PANEL = "[data-fa-mega-panel]";
const SCRIM = "[data-fa-mega-menu-scrim]";
const TRIGGER = "[data-fa-nav-menu-trigger]";
const GROUP = "[data-fa-menu-group]";
const TOGGLE = "[data-fa-menu-toggle]";

const OPEN = "fa-mega-menu--open";
const ENTERING = "fa-mega-menu--entering";

const HEIGHT = "--fa-mega-height";
const TOP = "--fa-mega-top";
const LIFT = "--fa-mega-lift";
const GROUP_ORIGIN = "--fa-menu-group-origin";
const SHIFT = "--fa-mega-shift-sign";
const SHIFT_DISTANCE = "--fa-mega-shift";
const COLUMNS = "[data-fa-mega-panel-columns]";
const MOVING = "[data-fa-mega-panel-columns], [data-fa-menu-callout]";

const DESKTOP = "(width >= 80rem)";

const initOne = (
	root: HTMLElement,
	mega: HTMLElement,
	heldFocus: () => boolean,
	signal: AbortSignal,
): (() => void) => {
	const sheet = mega.querySelector<HTMLElement>(SHEET);

	if (!sheet) return () => undefined;

	const fromUtility = mega.getAttribute(ORIGIN) === "utility";
	const panelKey = fromUtility ? EPanel.UTILITY : EPanel.MEGA;

	const panels = [...mega.querySelectorAll<HTMLElement>(PANEL)];
	const ids = new Set(panels.map((panel) => panel.id));

	/* Filtered by the panel each one names, not by proximity: both megamenus hang off the header
	   root, so an unfiltered list hands each of them the other's triggers. */
	const triggers = [...root.querySelectorAll<HTMLElement>(TRIGGER)].filter((trigger) =>
		ids.has(trigger.getAttribute("aria-controls") ?? ""),
	);

	if (panels.length === 0 || triggers.length === 0) return () => undefined;

	let exitFrame: number | undefined;

	const panelFor = (trigger: Element) =>
		panels.find((panel) => panel.id === trigger.getAttribute("aria-controls"));

	const openTrigger = () =>
		triggers.find((trigger) => trigger.getAttribute("aria-expanded") === "true");

	/* The bar's own edge, not the header's height: the collapse translates the bar up and leaves
	   the header box at its full height, so the sheet would hang off a row that has moved. */
	const bar = () => {
		const row = root.querySelector<HTMLElement>(BAR);

		return row ? row.getBoundingClientRect().bottom - root.getBoundingClientRect().top : 0;
	};

	const sheetHeight = () => sheet.getBoundingClientRect().height;

	const wipe = createWipeOrigin({
		elements: panels.flatMap((panel) => [...panel.querySelectorAll<HTMLElement>(GROUP)]),
		property: GROUP_ORIGIN,
	});

	const store = getHeaderStore(root);

	/* The sheet, not the container: `settled` waits on the element's own transition, and the
	   container declares none — so the handover fell to the store's 1000ms fallback. */
	const lifted = () => store.read().bar !== EBar.INITIAL;

	const utilityEdge = () => {
		const row = root.querySelector<HTMLElement>(UTILITY_ROW);

		return row ? row.getBoundingClientRect().bottom - root.getBoundingClientRect().top : 0;
	};

	/* One column step, as the prototype measures it, so the switch reads as the columns moving
	   into their neighbours' places. Set on the panel, not the columns: the callout is a sibling
	   and would otherwise inherit nothing and fall back to a different distance. */
	const stride = (panel: HTMLElement) => {
		const columns = panel.querySelector<HTMLElement>(COLUMNS);
		const [first, second] = [...(columns?.children ?? [])];

		if (!(first instanceof HTMLElement) || !(second instanceof HTMLElement)) return;

		const distance = second.offsetLeft - first.offsetLeft;

		if (distance > 0) panel.style.setProperty(SHIFT_DISTANCE, `${String(distance)}px`);
	};

	/* Paints the closed state with its transitions off, so the aimed offset becomes the panel's
	   start rather than a target the live state immediately retargets from. */
	const paintClosed = (panel: HTMLElement) => {
		const moving = [...panel.querySelectorAll<HTMLElement>(MOVING)];

		for (const node of moving) node.style.transitionProperty = "none";

		void panel.offsetWidth;

		for (const node of moving) node.style.removeProperty("transition-property");
	};

	const place = (panel: HTMLElement) => {
		const top = fromUtility ? utilityEdge() : bar();

		const drop = fromUtility || lifted() ? top : 0;

		sheet.style.setProperty(TOP, `${String(top)}px`);
		mega.style.setProperty(LIFT, `${String(drop)}px`);
		sheet.style.setProperty(HEIGHT, `${String(top - drop + panel.offsetHeight)}px`);
	};

	const measure = (panel: HTMLElement) => {
		stride(panel);
		place(panel);
	};

	const dropExit = () => {
		if (exitFrame !== undefined) window.cancelAnimationFrame(exitFrame);
		exitFrame = undefined;
	};

	const watch = (step: () => void) => {
		dropExit();

		/* Next frame, never this one: measuring in the same task reads the height before the
		   transition has started from it. */
		exitFrame = window.requestAnimationFrame(step);
	};

	const requeue = (step: () => void) => {
		exitFrame = signal.aborted ? undefined : window.requestAnimationFrame(step);
	};

	const exit = () => {
		if (sheetHeight() > 0) {
			requeue(exit);
			return;
		}

		exitFrame = undefined;
		sheet.style.removeProperty(TOP);
		mega.style.removeProperty(LIFT);
	};

	const isOpen = () => mega.classList.contains(OPEN);

	const shut = (keepHeight = false) => {
		mega.classList.remove(OPEN, ENTERING);

		for (const trigger of triggers) trigger.setAttribute("aria-expanded", "false");
		for (const panel of panels) panel.inert = true;

		if (!keepHeight) sheet.style.removeProperty(HEIGHT);
	};

	const field = createPanel({
		key: panelKey,
		root,
		settles: sheet,
		returnTo: openTrigger,
		isOpen,
		release: () => {
			shut();
			watch(exit);
		},
	});

	const close = field.close;

	/* Opposite signs: the outgoing leaves the way the row is travelling and the incoming starts
	   from the side it is travelling from, so the pair reads as one move rather than two. */
	const aim = (incoming: HTMLElement, from: HTMLElement | undefined) => {
		/* Nothing to travel from, so the first open fades in place — a slide with no outgoing
		   panel beside it reads as the panel arriving from nowhere. */
		if (from === undefined) {
			panelFor(incoming)?.style.setProperty(SHIFT, "0");
			return;
		}

		const forward = triggers.indexOf(incoming) > triggers.indexOf(from);

		panelFor(from)?.style.setProperty(SHIFT, forward ? "-1" : "1");
		panelFor(incoming)?.style.setProperty(SHIFT, forward ? "1" : "-1");
	};

	const open = (trigger: HTMLElement) => {
		const panel = panelFor(trigger);

		if (!panel) return;

		/* `false`: only the root click handler reaches here, so the trigger already has focus. */
		if (openTrigger() === trigger) {
			close(false);
			return;
		}

		const switching = isOpen();

		aim(trigger, openTrigger());

		shut(switching);
		store.setPanel(panelKey);

		field.hold();

		/* Before the panel is live, so the stride is already correct when the transition starts —
		   writing it afterwards retargets a translate that is already travelling. */
		stride(panel);

		paintClosed(panel);

		panel.inert = false;
		panel.scrollTop = 0;
		trigger.setAttribute("aria-expanded", "true");

		mega.classList.toggle(ENTERING, !switching);
		mega.classList.add(OPEN);

		/* After the class and never before the flush: the closed rule holds height discrete for
		   its exit delay, so a height written while shut clips the sheet to zero through the whole
		   fade in and the panel appears at the end of it. */
		place(panel);

		focusFirstStop(panel);
	};

	const options = { signal };

	root.addEventListener(
		"click",
		(event) => {
			if (!(event.target instanceof Element)) return;

			const trigger = event.target.closest<HTMLElement>(TRIGGER);

			if (trigger) {
				if (triggers.includes(trigger)) open(trigger);
				return;
			}

			if (event.target.closest(SCRIM) && isOpen()) close(true);
		},
		options,
	);

	createDisclosure({ scope: mega, contains: mega, isOpen, close, signal });

	mega.addEventListener(
		"keydown",
		(event) => {
			if (event.key !== "Tab" || !isOpen()) return;

			const panel = panels.find((one) => !one.inert);

			if (!panel) return;

			const stops = tabStops(panel);
			const edge = event.shiftKey ? stops[0] : stops.at(-1);

			if (edge === undefined || edge !== document.activeElement) return;

			event.preventDefault();
			close(true);
		},
		options,
	);

	const desktop = window.matchMedia(DESKTOP);

	desktop.addEventListener(
		"change",
		(event) => {
			if (event.matches || !isOpen()) return;

			const held = heldFocus();

			close(false);

			if (held) root.querySelector<HTMLElement>(TOGGLE)?.focus();
		},
		options,
	);

	const resize = new ResizeObserver(() => {
		const trigger = openTrigger();
		const panel = trigger ? panelFor(trigger) : undefined;

		if (panel) measure(panel);
	});

	resize.observe(root);

	return () => {
		field.dispose();
		wipe();

		for (const panel of panels) {
			panel.style.removeProperty(SHIFT);
			panel.style.removeProperty(SHIFT_DISTANCE);
		}

		resize.disconnect();
		dropExit();
		sheet.style.removeProperty(TOP);
		mega.style.removeProperty(LIFT);
	};
};

/** Returns its restore, which the header's teardown owns — listeners ride the caller's signal. */
export const initMega = (root: HTMLElement, signal: AbortSignal): (() => void) => {
	const heldFocus = trackFocusWithin(root, signal);
	const restores = [...root.querySelectorAll<HTMLElement>(MEGA)].map((mega) =>
		initOne(root, mega, heldFocus, signal),
	);

	return () => {
		for (const restore of restores) restore();
	};
};
